/*
 * newsbot - forked from autobrr
 */

import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { lazy, Suspense } from "react";

import { Header } from "@components/header";
import { NotFound } from "@components/alerts/NotFound";
import { ErrorPage } from "@components/alerts";
import { queryClient } from "@api/QueryClient";
import { supabase } from "@/lib/supabase";

// Screens
import { Dashboard } from "@screens/Dashboard";
import { Stories } from "@screens/Stories";
import { Trends } from "@screens/Trends";
import { StoryDetail } from "@screens/StoryDetail";
import { Filters } from "@screens/Filters";
import { FilterDetails } from "@screens/filters/FilterDetails";
import { Suggestions } from "@screens/Suggestions";
import { Logs } from "@screens/Logs";
import { Settings } from "@screens/Settings";
import FeedSettings from "@screens/settings/Feed";
import PromptsSettings from "@screens/settings/Prompts";
import UsersSettings from "@screens/settings/Users";
import AccountSettings from "@screens/settings/Account";
import BatchStatus from "@screens/settings/BatchStatus";
import Blocklist from "@screens/settings/Blocklist";
import { Login } from "@screens/auth/Login";
import { Onboarding } from "@screens/auth/Onboarding";

// Root layout - no header for auth pages
const RootComponent = () => (
  <Outlet />
);

const RootRoute = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

// Login route
const LoginRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "login",
  component: Login,
});

// Onboarding route
const OnboardRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "onboard",
  component: Onboarding,
});

// Auth guard route - all protected routes are children of this
const AuthRoute = createRoute({
  getParentRoute: () => RootRoute,
  id: "auth",
  beforeLoad: async ({ location }) => {
    // Check if onboarding is needed first
    try {
      const response = await fetch("/api/v1/auth/check");
      if (response.ok) {
        const data = await response.json();
        if (data.needs_onboarding) {
          throw redirect({
            to: "/onboard",
          });
        }
      }
    } catch (error) {
      // If it's a redirect (has 'to' property), re-throw it
      if (error && typeof error === "object" && "to" in error) {
        throw error;
      }
      // Otherwise continue to auth check (API might be down)
    }

    // Check if user is authenticated via Supabase
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }

    return { session };
  },
});

// Lazy-load devtools so they're excluded from production bundles entirely
const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-router-devtools").then((m) => ({
        default: m.TanStackRouterDevtools,
      }))
    )
  : () => null;

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : () => null;

function DevTools() {
  return (
    <Suspense fallback={null}>
      <TanStackRouterDevtools position="bottom-right" />
      <ReactQueryDevtools initialIsOpen={false} />
    </Suspense>
  );
}

// Authenticated layout with header
function AuthenticatedLayout() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <Outlet />
      {import.meta.env.DEV && <DevTools />}
    </div>
  );
}

const AuthLayoutRoute = createRoute({
  getParentRoute: () => AuthRoute,
  id: "authenticated-layout",
  component: AuthenticatedLayout,
});

// Dashboard
const DashboardRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "/",
  component: Dashboard,
});

// Stories
export const StoriesRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "stories",
  component: Stories,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Number(search.page) || 0,
    sort: (search.sort as string) || "last_article_at",
    dir: (search.dir as string) || "desc",
    q: (search.q as string) || "",
    minImportance: search.minImportance !== undefined && search.minImportance !== "" ? Number(search.minImportance) : undefined,
    category: (search.category as string) || "",
    tags: (search.tags as string) || "",
    vertical: (search.vertical as string) || "",
  }),
});

// Trends
const TrendsRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "/trends",
  component: Trends,
});

// Story Detail
const StoryDetailRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "stories/$storyId",
  component: StoryDetail,
});

// Filters (formerly Categories)
const FiltersRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "filters",
  component: Filters,
});

// Filter Details
const FilterDetailsRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "filters/$filterId",
  component: FilterDetails,
});

// Redirect /categories to /filters for backward compatibility
const CategoriesRedirectRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "categories",
  beforeLoad: () => {
    throw redirect({ to: "/filters" });
  },
});

// Suggestions
const SuggestionsRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "suggestions",
  component: Suggestions,
});

const LogsRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "logs",
  component: Logs,
});

const SettingsRoute = createRoute({
  getParentRoute: () => AuthLayoutRoute,
  path: "settings",
  component: Settings,
});

const SettingsIndexRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "/",
  component: FeedSettings,
});

const SettingsFeedsRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "feeds",
  component: FeedSettings,
});

const SettingsPromptsRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "prompts",
  component: PromptsSettings,
});

const SettingsUsersRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "users",
  component: UsersSettings,
});

const SettingsAccountRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "account",
  component: AccountSettings,
});

const SettingsBatchesRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "batches",
  component: BatchStatus,
});

const SettingsBlocklistRoute = createRoute({
  getParentRoute: () => SettingsRoute,
  path: "blocklist",
  component: Blocklist,
});

const settingsRouteTree = SettingsRoute.addChildren([
  SettingsIndexRoute,
  SettingsFeedsRoute,
  SettingsPromptsRoute,
  SettingsUsersRoute,
  SettingsAccountRoute,
  SettingsBatchesRoute,
  SettingsBlocklistRoute,
]);

const authRouteTree = AuthRoute.addChildren([
  AuthLayoutRoute.addChildren([
    DashboardRoute,
    StoriesRoute,
    TrendsRoute,
    StoryDetailRoute,
    FiltersRoute,
    FilterDetailsRoute,
    CategoriesRedirectRoute,
    SuggestionsRoute,
    LogsRoute,
    settingsRouteTree,
  ]),
]);

const routeTree = RootRoute.addChildren([
  LoginRoute,
  OnboardRoute,
  authRouteTree,
]);

export const router = createRouter({
  routeTree,
  defaultErrorComponent: (ctx) => <ErrorPage error={ctx.error} reset={ctx.reset} />,
  defaultPendingComponent: () => (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
    </div>
  ),
  defaultPendingMs: 200,
  context: {
    queryClient,
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
